import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, CheckCircle2, AlertTriangle, RefreshCw, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Owner/admin-only chat-model health pill.
 * Shows `✓ All models healthy` (green) or `⚠ N down` (red).
 * Tap to expand → per-model status with latency + refresh button.
 * The backend re-probes every 10 minutes and pushes a notification to the owner
 * the moment any model goes down or recovers.
 */
export default function ModelHealthPill() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async ({ force = false } = {}) => {
    if (force) setRefreshing(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/chat-models/health${force ? '?refresh=true' : ''}`, { withCredentials: true });
      setData(data);
    } catch {
      // not admin or other — silently hide
      setData(null);
    } finally {
      if (force) setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh from cache every 60s so the pill stays in sync without re-probing
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  if (!data) return null; // not admin or initial load failed → don't render

  const allHealthy = data.all_healthy;
  const broken = (data.models || []).filter((m) => !m.ok);
  const pending = data.pending;

  return (
    <div className="px-4 pt-2" data-testid="model-health-pill">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border text-xs transition-colors ${
          pending
            ? 'bg-[#52525B]/10 border-[#52525B]/20 text-[#A1A1AA]'
            : allHealthy
              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/15'
              : 'bg-[#E11D48]/15 border-[#E11D48]/35 text-[#FDA4AF] hover:bg-[#E11D48]/20 animate-pulse'
        }`}
        data-testid="model-health-pill-toggle"
      >
        {pending ? (
          <Activity className="w-3.5 h-3.5" />
        ) : allHealthy ? (
          <CheckCircle2 className="w-3.5 h-3.5" />
        ) : (
          <AlertTriangle className="w-3.5 h-3.5" />
        )}
        <span className="flex-1 text-left font-medium">
          {pending
            ? 'Probing chat models…'
            : allHealthy
              ? `All ${data.total} chat models healthy`
              : `${data.broken_count} of ${data.total} chat model${data.broken_count > 1 ? 's' : ''} down`}
        </span>
        <span className="text-[10px] uppercase tracking-wider opacity-70">{open ? 'hide' : 'details'}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-2 p-3 rounded-xl bg-white/[0.03] border border-white/5 space-y-1.5" data-testid="model-health-details">
          {(data.models || []).map((m) => (
            <div
              key={m.model_id}
              className="flex items-center gap-2 text-[11px]"
              data-testid={`model-row-${m.model_id}`}
            >
              {m.ok ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-[#E11D48] flex-shrink-0" />
              )}
              <span className="capitalize text-white font-medium w-16 flex-shrink-0">{m.model_id}</span>
              <span className="text-[10px] text-[#71717A] truncate flex-1">
                {m.ok ? m.openrouter_model : `${m.openrouter_model} — ${m.error || 'unknown error'}`}
              </span>
              {m.ok && <span className="text-[10px] text-[#52525B] flex-shrink-0">{m.latency_ms}ms</span>}
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/5">
            <p className="text-[10px] text-[#52525B]">
              {data.checked_at ? `Last checked ${new Date(data.checked_at).toLocaleTimeString()}` : 'Not yet checked'} · auto-rechecks every 10 min
            </p>
            <button
              type="button"
              onClick={() => load({ force: true })}
              disabled={refreshing}
              className="flex items-center gap-1 text-[10px] text-[#A1A1AA] hover:text-white px-2 py-1 rounded-md hover:bg-white/5 transition-colors disabled:opacity-50"
              data-testid="model-health-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Probing…' : 'Re-probe now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
